import { config as faConfig } from '@fortawesome/fontawesome-svg-core'

// Stop FontAwesome's runtime from injecting its stylesheet via DOM mutation —
// we ship it via the bundled CSS file (see src/layouts/index.tsx).
faConfig.autoAddCss = false

declare global {
  interface Window {
    _hsq: unknown[]
    hljs?: { highlightAll: () => void; highlightElement: (el: Element) => void }
    __hljsReady?: Promise<void>
  }
}

const HLJS_VERSION = '11.9.0'
const HLJS_THEME_URL = `https://cdnjs.cloudflare.com/ajax/libs/highlight.js/${HLJS_VERSION}/styles/github-dark.min.css`
const HLJS_SCRIPT_URL = `https://cdnjs.cloudflare.com/ajax/libs/highlight.js/${HLJS_VERSION}/highlight.min.js`

const loadHighlightJs = (): Promise<void> => {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.__hljsReady) return window.__hljsReady

  window.__hljsReady = new Promise<void>((resolve) => {
    // Theme stylesheet
    if (!document.getElementById('hljs-theme')) {
      const link = document.createElement('link')
      link.id = 'hljs-theme'
      link.rel = 'stylesheet'
      link.href = HLJS_THEME_URL
      document.head.appendChild(link)
    }

    // Library
    const existing = document.getElementById('hljs-script') as HTMLScriptElement | null
    if (existing) {
      if (window.hljs) resolve()
      else existing.addEventListener('load', () => resolve())
      return
    }
    const script = document.createElement('script')
    script.id = 'hljs-script'
    script.src = HLJS_SCRIPT_URL
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => resolve() // never hang the page on a CDN miss
    document.head.appendChild(script)
  })

  return window.__hljsReady
}

const runHighlight = () => {
  if (typeof window !== 'undefined' && window.hljs) {
    try {
      window.hljs.highlightAll()
    } catch {
      // Swallow — highlighting is decorative, never block rendering
    }
  }
}

export const onClientEntry = async () => {
  // IntersectionObserver polyfill for gatsby-background-image (Safari, IE)
  if (typeof window.IntersectionObserver === `undefined`) {
    await import('intersection-observer')
  }

  // Kick off highlight.js load — it'll resolve in the background and the
  // first highlightAll() call happens on the initial route update.
  loadHighlightJs().then(runHighlight)

  // Web Vitals: load lazily so it doesn't block the main bundle, then wire
  // up the v5 metric reporters (CLS, INP, LCP, FCP, TTFB). FID is retired.
  if ('requestIdleCallback' in window) {
    ;(window as unknown as { requestIdleCallback: (cb: () => void) => void })
      .requestIdleCallback(() => {
        import('web-vitals').then(
          ({ onCLS, onINP, onLCP, onFCP, onTTFB }) => {
            import('./src/utils/performance').then(({ reportWebVital }) => {
              onCLS(reportWebVital)
              onINP(reportWebVital)
              onLCP(reportWebVital)
              onFCP(reportWebVital)
              onTTFB(reportWebVital)
            })
          },
        )
      })
  }
}

export const onRouteUpdate = ({ location }) => {
  if (typeof window !== 'undefined') {
    // Use requestIdleCallback to defer HubSpot tracking
    const trackHubSpot = () => {
      window._hsq = window._hsq || []
      window._hsq.push(['setPath', location.pathname])
      window._hsq.push(['trackPageView'])
    }

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(trackHubSpot)
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(trackHubSpot, 100)
    }

    // Re-run syntax highlighting whenever a new page becomes active.
    // Wait one frame so MDX content is fully in the DOM, then highlight.
    loadHighlightJs().then(() => {
      window.requestAnimationFrame(runHighlight)
    })
  }
}
export { wrapRootElement } from './wrap-root-element'
